// ============================================================================
// LES RÉGLAGES DU WALLET — les types de titre qui partent, leurs clés, et la règle « un gabarit par
// type ».
//
// ⚠️ IL VIT DANS LE CONTRAT parce que les deux bouts en dépendent : le balayage du back-office qui
// envoie les coupons et l'écran du back-office qui les règle. Écrits de chaque côté, ils divergeraient
// au premier ajout (§24.3bis).
// ⚠️ LES DEUX INTERRUPTEURS DU WALLET N'ONT PAS DE TABLE À EUX : ils sont lus comme tous les autres, par
// `switchOn` et `SWITCH_DEFAULTS` (`settings-catalog.ts`) — deux tables d'interrupteurs finiraient par
// ne plus dire le même défaut.
// ============================================================================

/**
 * LES TYPES DE TITRE QUI PEUVENT PARTIR — liste FERMÉE, et c'est elle qui décide des réglages.
 *
 * ⚠️ `coupon` N'Y EST PAS : c'est une remise marketing, elle ne naît pas d'un geste de caisse et n'a
 * donc aucun titre à refléter.
 * ⚠️ Chaque type a SON réglage de gabarit : dans le portefeuille du client, un chèque cadeau et un bon
 * de fidélité doivent se distinguer — sinon il ne sait pas ce qu'il tient.
 */
export const COUPON_TITLE_TYPES = ['cadeau', 'fidelite', 'avoir', 'acompte'] as const;
export type CouponTitleType = (typeof COUPON_TITLE_TYPES)[number];

/** La clé de réglage du gabarit d'un type — DÉRIVÉE, jamais réécrite ailleurs. */
export const couponTemplateKey = (type: CouponTitleType): string => `wallet.coupon_template.${type}`;

// ---------------------------------------------------------------------------
// LES GROUPES DE RÉGLAGES À VALEURS DISTINCTES
// ---------------------------------------------------------------------------

/**
 * Un groupe de réglages dont deux clés ne portent JAMAIS la même valeur non vide.
 *
 * ⚠️ UNE DONNÉE DU CONTRAT, PAS UNE RÈGLE D'ÉCRAN : l'écran grise le choix déjà pris, le serveur
 * refuse l'écriture qui l'ignorerait. Une règle tenue par l'écran seul ne protège que le chemin qui y
 * passe — une requête écrite à la main, ou un second écran, la traverserait sans un mot.
 */
export interface DistinctSettingGroup {
  /** Identifiant stable — il nomme le groupe dans les refus. */
  readonly id: string;
  readonly keys: readonly string[];
  /** Pourquoi deux clés ne partagent jamais une valeur — rendu tel quel dans le motif du refus. */
  readonly why: string;
}

export const DISTINCT_SETTING_GROUPS: readonly DistinctSettingGroup[] = [
  {
    id: 'wallet.coupon_template',
    keys: COUPON_TITLE_TYPES.map(couponTemplateKey),
    why: 'deux types de titre sous le même gabarit, le client ne sait plus ce qu’il tient dans son portefeuille',
  },
];

/** Deux réglages d'un groupe qui porteraient la même valeur après une écriture. */
export interface DistinctSettingConflict {
  group: DistinctSettingGroup;
  /** La valeur partagée, telle que comparée (sans espaces de bord). */
  value: string;
  /** Toutes les clés qui la porteraient, dans l'ordre du groupe. */
  keys: string[];
  /** Celles que CE lot écrit — c'est à elles que le refus s'adresse. */
  written: string[];
}

/**
 * La valeur COMPARÉE : sans espaces de bord, et le vide n'est jamais en conflit.
 * ⚠️ Le balayage du back-office lit le gabarit sans ses espaces de bord (`templatesConfigures`) : deux
 * valeurs qui ne diffèrent que par eux désignent le MÊME gabarit, donc elles se comparent ainsi.
 */
const comparable = (v: string | null | undefined): string => (v ?? '').trim();
const has = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

/**
 * LES CONFLITS QU'UN LOT INTRODUIT — l'état de la base, recouvert par ce que le lot écrit.
 *
 * ⚠️ 🔴 LE LOT ENTIER ET L'ÉTAT DE LA BASE, ENSEMBLE. Jugé ligne à ligne, un lot qui ÉCHANGE les
 * gabarits de deux types serait refusé à tort, et un lot qui pose le même gabarit sur deux types
 * jamais réglés passerait.
 * ⚠️ UN DOUBLON HÉRITÉ QUE CE LOT NE TOUCHE PAS N'EST PAS RAPPORTÉ : il ne bloque pas un
 * enregistrement voisin (activer le wallet, régler un autre type). L'écran le montre, et le prochain
 * geste sur l'une des deux clés le tranche.
 */
export function distinctSettingConflicts(
  current: Readonly<Record<string, string | null | undefined>>,
  incoming: Readonly<Record<string, string>>,
): DistinctSettingConflict[] {
  const out: DistinctSettingConflict[] = [];
  for (const group of DISTINCT_SETTING_GROUPS) {
    const touched = group.keys.filter((k) => has(incoming, k));
    if (touched.length === 0) continue;
    const byValue = new Map<string, string[]>();
    for (const k of group.keys) {
      const v = comparable(has(incoming, k) ? incoming[k] : current[k]);
      if (v === '') continue;
      byValue.set(v, [...(byValue.get(v) ?? []), k]);
    }
    for (const [value, keys] of byValue) {
      if (keys.length < 2) continue;
      const written = keys.filter((k) => touched.includes(k));
      if (written.length > 0) out.push({ group, value, keys, written });
    }
  }
  return out;
}

/**
 * QUI PORTE DÉJÀ CETTE VALEUR, dans le groupe de ce réglage — les AUTRES clés, dans l'ordre du groupe.
 * Vide quand le réglage n'appartient à aucun groupe, ou que la valeur est vide.
 */
export function distinctSettingHolders(
  values: Readonly<Record<string, string | null | undefined>>,
  name: string,
  candidate: string | null | undefined,
): string[] {
  const v = comparable(candidate);
  if (v === '') return [];
  const group = DISTINCT_SETTING_GROUPS.find((g) => g.keys.includes(name));
  if (!group) return [];
  return group.keys.filter((k) => k !== name && comparable(values[k]) === v);
}
